import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import PrintButton from "@components/FlowActionButtons/PrintButton";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useNotification } from "@context/NotificationContext";
import type { FinalDelayReport } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useFinalDelayReport } from "../hooks/useFinalDelayReport";
import { formatInstant } from "../utils/formatInstant";
import { problemMessage } from "../utils/problemDetail";
import {
  formatHours,
  formatQuantity,
  formatReportDate,
  formatSurvey,
  formatWhole,
  splitInstant,
} from "./reportFormat";
import { usePrintReport } from "./usePrintReport";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.final-report`;
const D = `${L}.delay`;
const TABLE = "min-w-full text-sm border-collapse";
const HEAD = "border-b border-gray-300 px-2 py-1 text-left font-semibold whitespace-nowrap";
const CELL = "border-b border-gray-200 px-2 py-1 whitespace-nowrap";
const NUM = "text-right";

const dash = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const Row: React.FC<{ testId: string; label: string; value: React.ReactNode }> = ({
  testId,
  label,
  value,
}) => (
  <div className="flex justify-between gap-4 border-b border-gray-200 py-1 text-sm">
    <span className="text-gray-600">{label}</span>
    <span data-testid={testId}>{value}</span>
  </div>
);

const Field: React.FC<{ testId: string; label: string; value: React.ReactNode }> = ({
  testId,
  label,
  value,
}) => (
  <div className="flex flex-col gap-0.5 rounded border border-gray-200 px-3 py-2">
    <span className="text-xs text-gray-600">{label}</span>
    <span data-testid={testId} style={{ overflowWrap: "anywhere" }}>
      {value}
    </span>
  </div>
);

const Header: React.FC<{ report: FinalDelayReport }> = ({ report }) => {
  const { t } = useTranslation();
  const header = report.header;
  const unavailable = t(`${D}.unavailable`);
  return (
    <section data-testid="final-delay-header" className="grid grid-cols-1 gap-2 md:grid-cols-3">
      <Field
        testId="final-delay-vessel"
        label={t(`${D}.header.vessel`)}
        value={dash(header.vesselName)}
      />
      <Field
        testId="final-delay-operation-number"
        label={t(`${D}.header.operation-number`)}
        value={dash(header.operationNumber)}
      />
      <Field
        testId="final-delay-report-date"
        label={t(`${D}.header.report-date`)}
        value={formatReportDate(header.reportDate)}
      />
      <Field
        testId="final-delay-loading-commenced"
        label={t(`${D}.header.loading-commenced`)}
        value={formatInstant(header.loadingCommenced)}
      />
      <Field
        testId="final-delay-loading-completed"
        label={t(`${D}.header.loading-completed`)}
        value={formatInstant(header.loadingCompleted)}
      />
      <Field
        testId="final-delay-ship-berthed"
        label={t(`${D}.header.ship-berthed`)}
        value={formatInstant(header.shipBerthed)}
      />
      <Field
        testId="final-delay-ship-departure"
        label={t(`${D}.header.ship-departure`)}
        value={header.shipDepartureUnavailable ? unavailable : formatInstant(header.shipDeparture)}
      />
      <Field
        testId="final-delay-destination"
        label={t(`${D}.header.destination`)}
        value={header.destination ?? (header.destinationUnavailable ? unavailable : "-")}
      />
      <Field
        testId="final-delay-used-hoppers"
        label={t(`${D}.header.used-hoppers`)}
        value={dash((header.usedHoppers ?? []).join(", "))}
      />
      <Field
        testId="final-delay-submitted-by"
        label={t(`${D}.header.submitted-by`)}
        value={dash(header.submittedBy)}
      />
    </section>
  );
};

const DelaysGrid: React.FC<{ report: FinalDelayReport }> = ({ report }) => {
  const { t } = useTranslation();
  return (
    <section data-testid="final-delay-grid-section" className="flex flex-col gap-2">
      <Typography variant="h6" component="h3">
        {t(`${D}.grid.title`)}
      </Typography>
      <div className="overflow-x-auto" data-testid="final-delay-grid-scroll">
        <table className={TABLE} data-testid="final-delay-grid">
          <thead>
            <tr>
              {["id", "date", "start", "end", "code"].map((key) => (
                <th key={key} className={HEAD}>
                  {t(`${D}.grid.columns.${key}`)}
                </th>
              ))}
              {report.delayTypes.map((type) => (
                <th
                  key={type.code}
                  className={`${HEAD} ${NUM}`}
                  title={type.name}
                  data-testid="final-delay-type-column"
                >
                  {type.code}
                </th>
              ))}
              <th className={`${HEAD} ${NUM}`}>{t(`${D}.grid.columns.total`)}</th>
              <th className={HEAD}>{t(`${D}.grid.columns.remarks`)}</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => {
              const start = splitInstant(row.startAt);
              const end = splitInstant(row.endAt);
              return (
                <tr key={row.delayId} data-testid={`final-delay-row-${row.no}`}>
                  <td className={CELL}>{row.no}</td>
                  <td className={CELL}>{start.date}</td>
                  <td className={CELL}>{start.time}</td>
                  <td className={CELL}>{end.time}</td>
                  <td className={CELL}>{`${row.delayCode} - ${row.codeDescription}`}</td>
                  {row.minutesByType.map((minutes, index) => (
                    <td
                      key={report.delayTypes[index]?.code ?? index}
                      className={`${CELL} ${NUM}`}
                      data-testid={`final-delay-row-${row.no}-type-${index}`}
                    >
                      {minutes ? minutes : "-"}
                    </td>
                  ))}
                  <td className={`${CELL} ${NUM}`}>{row.durationMinutes}</td>
                  <td className={CELL}>{dash(row.remarks)}</td>
                </tr>
              );
            })}
            <tr className="font-semibold bg-gray-50" data-testid="final-delay-totals">
              <td className={CELL} colSpan={5}>
                {t(`${D}.grid.totals`)}
              </td>
              {report.totalsByType.map((total, index) => (
                <td key={report.delayTypes[index]?.code ?? index} className={`${CELL} ${NUM}`}>
                  {total ? total : "-"}
                </td>
              ))}
              <td className={`${CELL} ${NUM}`} data-testid="final-delay-total-minutes">
                {report.totalMinutes}
              </td>
              <td className={CELL} />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
};

const Summary: React.FC<{ report: FinalDelayReport }> = ({ report }) => {
  const { t } = useTranslation();
  const performance = report.performance;
  const period = report.period;
  const periodLabel = `${period.month} ${period.year}`;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <section data-testid="final-delay-by-type" className="flex flex-col gap-1">
        <Typography variant="h6" component="h3">
          {t(`${D}.by-type.title`)}
        </Typography>
        {report.summaryByType.map((summary) => (
          <Row
            key={summary.code}
            testId={`final-delay-by-type-${summary.code}`}
            label={summary.code}
            value={formatHours(summary.hours)}
          />
        ))}
        <Row
          testId="final-delay-gross-total"
          label={t(`${D}.by-type.gross-total`)}
          value={formatHours(performance.totalDelayHours)}
        />
      </section>
      <section data-testid="final-delay-performance" className="flex flex-col gap-1">
        <Typography variant="h6" component="h3">
          {t(`${D}.performance.title`)}
        </Typography>
        <Row
          testId="final-delay-gross-time"
          label={t(`${D}.performance.gross-time`)}
          value={formatHours(performance.grossLoadingHours)}
        />
        <Row
          testId="final-delay-net-time"
          label={t(`${D}.performance.net-time`)}
          value={formatHours(performance.netLoadingHours)}
        />
        <Row
          testId="final-delay-gross-rate"
          label={t(`${D}.performance.gross-rate`)}
          value={formatWhole(performance.grossLoadingRate)}
        />
        <Row
          testId="final-delay-net-rate"
          label={t(`${D}.performance.net-rate`)}
          value={formatWhole(performance.netLoadingRate)}
        />
        <Row
          testId="final-delay-in-berth"
          label={t(`${D}.performance.in-berth`)}
          value={
            performance.inBerthHours === null
              ? t(`${D}.performance.not-available`)
              : formatHours(performance.inBerthHours)
          }
        />
        <Row
          testId="final-delay-draft-survey"
          label={t(`${D}.performance.draft-survey`)}
          value={formatSurvey(performance.draftSurvey)}
        />
        <Row
          testId="final-delay-belt-scale"
          label={t(`${D}.performance.belt-scale`)}
          value={formatQuantity(performance.beltScale)}
        />
        <Row
          testId="final-delay-difference"
          label={t(`${D}.performance.difference`)}
          value={formatQuantity(performance.difference)}
        />
        <Row
          testId="final-delay-difference-pct"
          label={t(`${D}.performance.difference-pct`)}
          value={formatQuantity(performance.differencePct)}
        />
        <Row
          testId="final-delay-average-gross"
          label={t(`${D}.performance.average-gross`, { period: periodLabel })}
          value={formatWhole(period.averageGrossLoadingRate)}
        />
        <Row
          testId="final-delay-average-net"
          label={t(`${D}.performance.average-net`, { period: periodLabel })}
          value={formatWhole(period.averageNetLoadingRate)}
        />
        <Row
          testId="final-delay-monthly"
          label={t(`${D}.performance.monthly`, { period: periodLabel })}
          value={formatQuantity(period.monthlyThroughput)}
        />
        <Row
          testId="final-delay-yearly"
          label={t(`${D}.performance.yearly`, { year: period.year })}
          value={formatQuantity(period.yearlyThroughput)}
        />
      </section>
    </div>
  );
};

/**
 * DBCO-0059 (R-32, AD-99) - the Final Delay Report, frozen at Complete: header, the delay grid with a column per delay type present,
 * Delays by type and the Operation Performance Summary with its period figures. Every figure is the API's; the client formats only.
 * A retired code shows with its stored description. Print fetches the report's PDF.
 */
const FinalDelayReportView: React.FC<{ operationId: string }> = ({ operationId }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const report = useFinalDelayReport(operationId, true);
  const print = usePrintReport("delay");

  useEffect(() => {
    if (report.isError) {
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(report.error) ?? t(`${L}.load-failed`),
      });
    }
  }, [report.isError, report.errorUpdatedAt, report.error, notify, t]);

  return (
    <Box
      data-testid="final-delay-report"
      sx={{ display: "flex", flexDirection: "column", gap: 3, position: "relative", minHeight: 80 }}
    >
      <LoadingOverlay loading={report.isLoading} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrintButton
          langBase={L}
          permission=""
          isActionAvailable
          onPrint={() => void print(operationId)}
        />
      </Box>
      {report.isError && (
        <Typography data-testid="final-report-unavailable" color="text.secondary">
          {t(`${L}.not-available`)}
        </Typography>
      )}
      {report.data && (
        <>
          <Header report={report.data} />
          <DelaysGrid report={report.data} />
          <Summary report={report.data} />
        </>
      )}
    </Box>
  );
};

export default FinalDelayReportView;
